/**
 * メールの画面の表示用の小さな関数（i18n を使うので mailLogic.ts = 純関数とは分ける）。
 */
import { errorText, intlLocale, translate, type MessageKey } from '../../i18n';
import { failureVariant, utcHmToLocal, type Every } from '../../mailLogic';

/**
 * 定期の説明（「毎週 金曜 20:00」）。時刻は UTC の at を現地時刻に直して出す。
 * 毎週の曜日・毎月の日にちは最初の回（sendAt）から取る（Core の確定した形: API に曜日・日にちの欄は無く、sendAt の曜日 / 日で続く）。
 * サーバーは UTC の曜日・日で数えるが、現地で見ても同じ曜日・日の同じ時刻になる（UTC の時刻が固定 = 現地との差も固定。夏時間を除く）
 */
export function everyLabel(every: Every, atUtc: string, firstIso: string | null | undefined): string {
  const first = firstIso ? new Date(firstIso) : null;
  const time = utcHmToLocal(atUtc, first ?? new Date());
  if (every === 'week' && first) {
    return translate('mail.everyLabel.week', { weekday: first.toLocaleDateString(intlLocale(), { weekday: 'long' }), time });
  }
  if (every === 'month' && first) return translate('mail.everyLabel.month', { day: first.getDate(), time });
  return translate('mail.everyLabel.day', { time });
}

const STATUS_KEYS: Record<string, MessageKey> = {
  draft: 'mail.status.draft',
  scheduled: 'mail.status.scheduled',
  sending: 'mail.status.sending',
  sent: 'mail.status.sent',
  canceled: 'mail.status.canceled',
  failed: 'mail.status.failed'
};
/** 配信の状態の名前（知らない状態はそのまま出す = サーバーが増やしても壊れない） */
export const statusLabel = (s: string) => (STATUS_KEYS[s] ? translate(STATUS_KEYS[s]) : s);

/**
 * 送れなかった理由（Core の failureReason）→ 文言。知らない理由はそのまま出す（サーバーが増やしても壊れない）。
 * WHY エラーの文言（mailErrorText）と分けるか: エラーは「今の操作が断られた」、こちらは「予約・定期の回が、その時刻に送れなかった」。
 *   後者は本人がその場にいない時に起きるので、何が起きたかを過去形で書き、次の一手（残高・差出人情報・運営）を添える
 */
const FAILURE_KEYS: Record<string, MessageKey> = {
  sender_incomplete: 'mail.failure.sender_incomplete',
  balance_due: 'mail.failure.balance_due',
  suspended: 'mail.failure.suspended',
  feature_disabled: 'mail.failure.feature_disabled',
  kids_audience: 'mail.failure.kids_audience',
  provider_error: 'mail.failure.provider_error',
  canceled: 'mail.failure.canceled',
  internal_error: 'mail.failure.internal_error',
  // 残高切れで取り消された定期（mailLogic.ts の failureVariant = 入金しても再開しない）
  balance_due_recurring: 'mail.failure.balance_due_recurring',
  project_deleted: 'mail.failure.project_deleted',
  account_closed: 'mail.failure.account_closed',
  owner_changed: 'mail.failure.owner_changed'
};
/** b を渡すと定期かどうかで文言を分ける（failureVariant の WHY）。知らない理由はそのまま */
export const failureText = (r: string, b: { recurrence?: unknown; parentId?: string | null } = {}) => {
  const v = failureVariant(r, b);
  return FAILURE_KEYS[v] ? translate(FAILURE_KEYS[v]) : r;
};

/**
 * メールの API のエラー → 文言。メールだけのコード（Core の確定した一覧）はメール向けの文言にする。
 * WHY `error.<code>` の共通の表に足さないか: idempotency_key_reused / balance_due はほかの機能でも使うコードで、
 *   not_draft / not_cancelable も一般的な名前（ほかの機能が同じコードを使い始めうる）。共通の表に置くとほかの画面に
 *   メールの話が出る。sender_incomplete / mail_suspended は
 *   メールだけのコードなので en.mail.ts の `error.<code>` のまま（errorText が自動で拾う）
 */
const MAIL_ERROR_KEYS: Partial<Record<string, MessageKey>> = {
  balance_due: 'mail.error.balance_due',
  kids_audience: 'mail.error.kids_audience',
  not_draft: 'mail.error.not_draft',
  not_cancelable: 'mail.error.not_cancelable',
  idempotency_key_reused: 'mail.error.idempotency_key_reused',
  mail_not_configured: 'mail.error.mail_not_configured',
  // 400 body_too_large（Core core/mail-footer: 本文の HTML が UTF-8 で 80,000 バイトまで）。名前が一般的なのでメールの表に置く（上の WHY）
  body_too_large: 'mail.error.body_too_large'
};
export const mailErrorText = (err: { code?: string; message: string; details?: Record<string, unknown> }) => errorText(err, MAIL_ERROR_KEYS);
